import React, { useState } from 'react';
import { Form } from '@ant-design/compatible';
import { Button } from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form';
import { useNavigate } from 'react-router-dom';

import { getLangKey } from 'models/Language';
import { setupProjectUrl } from 'api/ProjectApi';
import OnbProjectUrlInput, { DNSWarn } from 'components/onbProjectUrlInput';
import { HelpGuideBanner } from 'appJS/components/HelpGuideBanner';

import { trackVariantCreateProject } from '../analytics';

import * as translations from '../strings';
import {
  DEMO_SITE_URL,
  STEP_PATHS,
  WizardStep,
  WelcomeProject
} from '../types';
import styles from '../index.module.css';

interface Props extends FormComponentProps {
  project: WelcomeProject;
  onDevurlSaved: (url: string) => void;
  activeExperiments: string[];
}

type UrlValidation = {
  projectUrl?: { errors: Array<{ field: string; message: string }> };
};

type WebsiteChoice = 'yes' | 'no';

const YourProject: React.FC<Props> = ({
  project,
  form,
  onDevurlSaved,
  activeExperiments
}) => {
  const strings = translations[getLangKey()];
  const navigate = useNavigate();

  const [projectUrl, setProjectUrl] = useState<string>(project.devurl || '');
  const [updating, setUpdating] = useState<boolean>(false);
  const [websiteChoice, setWebsiteChoice] = useState<WebsiteChoice>('yes');
  const [updateError, setUpdateError] = useState<string>('');

  const stripUrl = (url: string) => {
    const parsedURL = new URL(url);
    parsedURL.pathname = '/';
    parsedURL.search = '';
    parsedURL.hash = '';
    return parsedURL.toString();
  };

  const saveUrlAndContinue = async (url: string) => {
    setUpdating(true);
    setUpdateError('');
    try {
      await setupProjectUrl({
        projectId: project.id,
        updateProjectData: {
          name: project.name,
          devurl: url,
          toggle_vertical_position: 90
        }
      });
      // Shared onboarding funnel event — mirrors the control flow's Step 1.
      trackVariantCreateProject(activeExperiments);
      onDevurlSaved(url);
      navigate(STEP_PATHS[WizardStep.INSTALL]);
    } catch (e) {
      if (e instanceof Error) setUpdateError(e.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    form.validateFields((validation: UrlValidation) => {
      const urlError = validation?.projectUrl?.errors?.[0];
      if (urlError && urlError.message !== DNSWarn) return;
      saveUrlAndContinue(stripUrl(projectUrl));
    });
  };

  const getContainer = () =>
    document.getElementById('welcome_to_bugherd_container');

  const choices: Array<{
    value: WebsiteChoice;
    heading: string;
    subtext: string;
  }> = [
    {
      value: 'yes',
      heading: strings.hasWebsiteYesHeading,
      subtext: strings.hasWebsiteYesSubtext
    },
    {
      value: 'no',
      heading: strings.hasWebsiteNoHeading,
      subtext: strings.hasWebsiteNoSubtext
    }
  ];

  return (
    <>
      <div className={styles.stepPill}>STEP 1</div>
      <h2 className={styles.stepHeading}>{strings.yourProjectHeading}</h2>
      <p className={styles.stepSubheading}>{strings.yourProjectSubheading}</p>

      <div className={styles.choiceTabs} role="radiogroup">
        {choices.map(({ value, heading, subtext }) => {
          const selected = websiteChoice === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setWebsiteChoice(value)}
              className={`${styles.choiceCard} ${
                selected ? styles.choiceCardSelected : ''
              }`}
            >
              <span
                className={`${styles.choiceRadio} ${
                  selected ? styles.choiceRadioSelected : ''
                }`}
              />
              <span className={styles.choiceCardInner}>
                <span className={styles.choiceHeading}>{heading}</span>
                <span className={styles.choiceSubtext}>{subtext}</span>
              </span>
            </button>
          );
        })}
      </div>

      {websiteChoice === 'yes' ? (
        <Form
          onSubmit={handleSubmit}
          hideRequiredMark
          layout="vertical"
          className={styles.urlForm}
        >
          <OnbProjectUrlInput
            parentForm={form}
            labelText={
              <span className={styles.urlLabel}>{strings.projectUrlLabel}</span>
            }
            placeholder={strings.projectUrlPlaceholder}
            currentUrl={projectUrl}
            setterCallback={setProjectUrl}
            container={getContainer}
          />
          {updateError && <p className={styles.error}>{updateError}</p>}
          <div className={styles.continueRow}>
            <Button
              type="primary"
              shape="round"
              size="large"
              htmlType="submit"
              loading={updating}
              disabled={!projectUrl}
              className={styles.continueButton}
            >
              {strings.continue}
            </Button>
          </div>
        </Form>
      ) : (
        <div className={styles.demoPanelWrap}>
          <HelpGuideBanner
            bubbleContent="💡"
            vertical
            message={
              <>
                No problem. We&apos;ll use <strong>demo-site.bugherd.com</strong> as
                your demo site to try BugHerd. Swap it out for your real site
                later, when ready.
              </>
            }
            button={{
              label: updating
                ? 'Setting up…'
                : `${strings.continueWithDemo} →`,
              action: () => {
                if (!updating) saveUrlAndContinue(DEMO_SITE_URL);
              }
            }}
          />
          {updateError && <p className={styles.error}>{updateError}</p>}
        </div>
      )}
    </>
  );
};

export default Form.create<Props>()(YourProject);
